import React from 'react';
import SEO from '../components/SEO';

const Disclosures = () => {
  return (
    <>
      <SEO 
        title="Disclosures | Paracca Financial Group"
        description="Important disclosures and regulatory information for Paracca Financial Group investment advisory services."
      />
      
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-[#0a69b2] text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-bellefair text-4xl sm:text-5xl uppercase mb-4">Disclosures</h1>
            <p className="font-montserrat text-base text-gray-100">
              Important Regulatory Information and Disclaimers
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none font-montserrat">
              
              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Firm Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Paracca Financial Group is a registered investment adviser providing investment advisory services to individuals, families, and institutions. We are registered with the appropriate regulatory authorities and operate as a fiduciary, meaning we are legally obligated to act in the best interests of our clients.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Regulatory Oversight:</strong> Our firm is subject to regulatory oversight and examination by the Securities and Exchange Commission (SEC) or state securities regulators, depending on the size of our assets under management.
                </p>
              </div>

              <div className="mb-8 bg-amber-50 border-l-4 border-amber-500 p-6">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Legal Services Disclaimer</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Important:</strong> Paracca Financial Group is not a law firm and does not provide legal advice. We are a registered investment advisory firm that provides financial planning and investment advisory services only.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For estate planning legal services, our in-house estate planning companies provide legal advice and documentation. These services are provided by licensed attorneys and are separate from our investment advisory services. Clients requiring legal advice will be referred to appropriate legal counsel.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Nothing on this website or in our communications should be construed as legal advice. For specific legal questions, please consult with a qualified attorney.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Form ADV</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our Form ADV is a comprehensive disclosure document that provides detailed information about our firm, including:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Our business practices and services offered</li>
                  <li>Fee schedules and compensation arrangements</li>
                  <li>Conflicts of interest and how we address them</li>
                  <li>Disciplinary history (if any)</li>
                  <li>Educational background and business experience of our key personnel</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You have the right to receive a copy of our Form ADV Part 2A (Firm Brochure) and Part 2B (Brochure Supplement for key personnel) at any time. These documents are also available on the SEC's Investment Adviser Public Disclosure website at <a href="https://adviserinfo.sec.gov" target="_blank" rel="noopener noreferrer" className="text-[#0a69b2] hover:underline">adviserinfo.sec.gov</a>.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Investment Risks</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All investments involve risk, and there is no guarantee that our investment strategies will be successful. Key risks include:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Market Risk:</strong> The value of securities can decrease due to changes in overall market conditions</li>
                  <li><strong>Interest Rate Risk:</strong> Changes in interest rates can affect the value of fixed-income securities</li>
                  <li><strong>Credit Risk:</strong> The issuer of a security may default on its obligations</li>
                  <li><strong>Liquidity Risk:</strong> Some securities may be difficult to sell quickly at a fair price</li>
                  <li><strong>Currency Risk:</strong> Foreign investments may be affected by currency exchange rate fluctuations</li>
                  <li><strong>Concentration Risk:</strong> Portfolios concentrated in specific sectors or securities face higher risk</li>
                  <li><strong>Options Risk:</strong> Options strategies involve complex risks and may result in substantial losses</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Past performance is not indicative of future results. You may lose money, including your principal investment.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Fiduciary Status</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  As a registered investment adviser, we act as a fiduciary to our advisory clients. This means:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>We have a legal duty to act in your best interest at all times</li>
                  <li>We are required to provide advice that is suitable for your individual circumstances</li>
                  <li>We must disclose all material conflicts of interest</li>
                  <li>We are obligated to seek best execution for your transactions</li>
                  <li>We maintain the confidentiality of your information</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Fee Structure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our investment advisory fees are typically based on a percentage of assets under management and are detailed in your Investment Advisory Agreement. Key points about our fees:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Fees are negotiable and may vary based on account size and complexity</li>
                  <li>Fees are generally billed quarterly in advance or arrears</li>
                  <li>You may incur additional fees such as custodian fees, transaction costs, and mutual fund or ETF expenses</li>
                  <li>We do not receive commissions on securities transactions in advisory accounts</li>
                  <li>You have the right to terminate the advisory agreement at any time</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For complete information about our fees and compensation, please refer to our Form ADV Part 2A.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Conflicts of Interest</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We are required to disclose any material conflicts of interest that could affect our relationship with you. We address conflicts through:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Full disclosure in our Form ADV and advisory agreements</li>
                  <li>Policies and procedures designed to ensure we act in your best interest</li>
                  <li>Regular review and monitoring of potential conflicts</li>
                  <li>Obtaining your informed consent when appropriate</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Custody and Safekeeping</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Your assets are held by a qualified custodian (typically a broker-dealer or bank) in your name. We do not take physical possession of your assets. You will receive account statements directly from the custodian at least quarterly. We encourage you to carefully review these statements and compare them with any reports you receive from us.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Proxy Voting</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Unless you have authorized us in writing to vote proxies on your behalf, you retain the right and responsibility to vote proxies for securities held in your account. If we have proxy voting authority, we will vote proxies in accordance with our written proxy voting policies and procedures, which are designed to act in your best interest. You may request a copy of our proxy voting policies and information about how we voted your proxies at any time.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Privacy and Confidentiality</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We are committed to protecting the privacy and confidentiality of your personal and financial information. We comply with federal and state privacy laws, including SEC Regulation S-P. For detailed information about our privacy practices, please review our <a href="/privacy" className="text-[#0a69b2] hover:underline">Privacy Policy</a>.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Business Continuity</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We maintain a business continuity plan designed to ensure that we can continue to provide services to you in the event of a significant business disruption. Our plan addresses backup systems, alternative communications, and recovery procedures.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Error Correction</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We have procedures in place to promptly identify and correct errors in your account. If an error occurs due to our actions or negligence, we will take appropriate steps to make your account whole. If you believe an error has occurred, please contact us immediately.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Client Responsibilities</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To help us provide you with the best possible service, you agree to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Provide accurate and complete information about your financial situation and goals</li>
                  <li>Promptly notify us of any material changes in your circumstances</li>
                  <li>Review all account statements and reports carefully and promptly report any discrepancies</li>
                  <li>Ask questions if you do not understand our recommendations or any aspect of your account</li>
                  <li>Maintain adequate records of your account and transactions</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Complaints and Disputes</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have a complaint or concern about our services, we encourage you to contact us directly so we can work to resolve the issue. If you are not satisfied with our response, you may also contact:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>SEC:</strong> Securities and Exchange Commission at <a href="https://www.sec.gov/complaint" target="_blank" rel="noopener noreferrer" className="text-[#0a69b2] hover:underline">www.sec.gov/complaint</a></li>
                  <li><strong>FINRA:</strong> Financial Industry Regulatory Authority (if applicable)</li>
                  <li><strong>State Regulator:</strong> Your state securities regulator</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on the terms of your advisory agreement, disputes may be subject to arbitration.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Additional Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This page provides a summary of important disclosures. For complete information about our firm and services, please review:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Our Form ADV Part 2A and Part 2B</li>
                  <li>Your Investment Advisory Agreement</li>
                  <li>Account statements and confirmations from your custodian</li>
                  <li>Any supplemental disclosures we provide</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We encourage you to ask questions and seek clarification on any matter you do not fully understand.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="font-bellefair text-2xl uppercase mb-4 text-[#0a69b2]">Contact Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For questions about these disclosures or to request additional information, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>Paracca Financial Group</strong></p>
                  <p className="text-gray-700 mb-2">428 S Tamiami Trail, Osprey, FL 34229</p>
                  <p className="text-gray-700 mb-2">Phone: <a href="tel:941-545-9057" className="text-[#0a69b2] hover:underline">941-545-9057</a></p>
                  <p className="text-gray-700">Email: <a href="mailto:contact@paracca.com" className="text-[#0a69b2] hover:underline">contact@paracca.com</a></p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-[#0a69b2] p-6 mt-8">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>Important:</strong> The information on this page is for informational purposes only and does not constitute legal or investment advice. You should consult with qualified professionals regarding your specific situation. All information is subject to change without notice.
                </p>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Disclosures;
